import * as React from 'react';
import { Counsellor } from '../../types/counsellor';
import { Typography, Box, Grid, TextField, Chip, Select, MenuItem, FormControl } from '@mui/material';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import AddressAutocomplete from '../common/AddressAutocomplete';
import { fetchAllPublicLookups } from '../../api/lookups';
import { Lookup } from '../../types/lookup';
import { toDateOnlyString } from '../../utils/dateUtils';

// Custom styles for DatePicker
const datePickerStyles = `
  .mui-datepicker {
    width: 100%;
    padding: 8px 12px;
    border-radius: 4px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    background-color: rgba(255, 255, 255, 0.05);
    color: rgba(255, 255, 255, 0.9);
    font-size: 14px;
  }
  .mui-datepicker:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.2);
  }
  .mui-datepicker::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

interface PersonalInformationProps {
  counsellor: Counsellor;
  isEditingPersonal: boolean;
  firstName: string;
  lastName: string;
  legalFirstName?: string;
  legalLastName?: string;
  email: string;
  phone: string;
  dob: string;
  address: string;
  addressLine1: string;
  addressLine2: string;
  addressLine3?: string;
  city: string;
  county: string;
  postcode: string;
  country: string;
  gender?: string;
  setFirstName: (value: string) => void;
  setLastName: (value: string) => void;
  setLegalFirstName?: (value: string) => void;
  setLegalLastName?: (value: string) => void;
  setEmail: (value: string) => void;
  setPhone: (value: string) => void;
  setDob: (value: string) => void;
  setAddress: (value: string) => void;
  setAddressLine1: (value: string) => void;
  setAddressLine2: (value: string) => void;
  setAddressLine3?: (value: string) => void;
  setCity: (value: string) => void;
  setCounty: (value: string) => void;
  setPostcode: (value: string) => void;
  setCountry: (value: string) => void;
  setGender?: (value: string) => void;
  handleSavePersonal: () => void;
  setIsEditingPersonal: (value: boolean) => void;
}

const PersonalInformation: React.FC<PersonalInformationProps> = ({
  counsellor,
  isEditingPersonal,
  firstName,
  lastName,
  legalFirstName,
  legalLastName,
  email,
  phone,
  dob,
  address,
  addressLine1,
  addressLine2,
  addressLine3,
  city,
  county,
  postcode,
  country,
  gender,
  setFirstName,
  setLastName,
  setLegalFirstName,
  setLegalLastName,
  setEmail,
  setPhone,
  setDob,
  setAddress,
  setAddressLine1,
  setAddressLine2,
  setAddressLine3,
  setCity,
  setCounty,
  setPostcode,
  setCountry,
  setGender,
  handleSavePersonal,
  setIsEditingPersonal,
}) => {
  const [lookups, setLookups] = React.useState<{ gender: Lookup[] }>({ gender: [] });

  React.useEffect(() => {
    const loadLookups = async () => {
      try {
        const data = await fetchAllPublicLookups();
        setLookups({ gender: data.gender || [] });
      } catch (error) {
        console.error('Failed to load lookups:', error);
      }
    };
    loadLookups();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>, setter: (value: string) => void): void => {
    setter(e.target.value);
  };

  const handleDateChange = (date: Date | null) => {
    if (date) {
      setDob(toDateOnlyString(date));
    }
  };

  const renderInput = (
    value: string,
    onChange: (value: string) => void,
    type: string = 'text',
    placeholder: string = '',
    disabled: boolean = false
  ) => {
    return isEditingPersonal ? (
      <TextField
        type={type}
        value={value || ''}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange(e, onChange)}
        placeholder={placeholder}
        variant="outlined"
        size="small"
        fullWidth
        disabled={disabled}
        sx={{
          mt: 1,
          '& .MuiOutlinedInput-root': {
            color: 'rgba(255, 255, 255, 0.9)',
            backgroundColor: disabled ? 'rgba(255, 255, 255, 0.02)' : 'rgba(255, 255, 255, 0.05)',
            '& fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.2)'
            },
            '&:hover fieldset': {
              borderColor: disabled ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.3)'
            },
            '&.Mui-focused fieldset': {
              borderColor: '#667eea'
            },
            '&.Mui-disabled': {
              color: 'rgba(255, 255, 255, 0.5)',
              '& fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.1)'
              }
            }
          },
          '& .MuiInputLabel-root': {
            color: 'rgba(255, 255, 255, 0.6)'
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: '#667eea'
          },
          '& .MuiOutlinedInput-input::placeholder': {
            color: 'rgba(255, 255, 255, 0.4)',
            opacity: 1
          }
        }}
      />
    ) : (
      <Typography variant="body1" className={value ? 'theme-text-primary' : 'theme-text-secondary'} sx={{ mt: 1 }}>
        {value || 'Not provided'}
      </Typography>
    );
  };

  return (
    <Box sx={{ mt: 3 }}>
      <style>{datePickerStyles}</style>
      <Grid container spacing={3}>
        {/* Personal Information and Address */}
        <Grid item xs={12}>
          <div className="glass-card p-6">
            <div className="flex justify-between items-center mb-6">
              <div></div>
              <div className="flex gap-4">
                {isEditingPersonal ? (
                  <>
                    <button
                      onClick={() => setIsEditingPersonal(false)}
                      className="px-4 py-2 text-sm font-bold theme-text-primary bg-white/10 border border-white/20 rounded-md shadow-sm hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-400 min-w-[160px] transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSavePersonal}
                      className="px-4 py-2 text-sm font-bold text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 min-w-[160px] transition-colors"
                    >
                      Save Changes
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => setIsEditingPersonal(true)}
                    className="px-4 py-2 text-sm font-bold text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 min-w-[160px] transition-colors"
                  >
                    Edit Information
                  </button>
                )}
              </div>
            </div>
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                {isEditingPersonal ? (
                  <>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom className="theme-text-primary">Name</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          type="text"
                          value={firstName || ''}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange(e, setFirstName)}
                          placeholder="First Name"
                          variant="outlined"
                          size="small"
                          fullWidth
                          sx={{
                            mt: 1,
                            '& .MuiOutlinedInput-root': {
                              color: 'rgba(255, 255, 255, 0.9)',
                              backgroundColor: 'rgba(255, 255, 255, 0.05)',
                              '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                              '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                              '&.Mui-focused fieldset': { borderColor: '#667eea' }
                            },
                            '& .MuiOutlinedInput-input::placeholder': {
                              color: 'rgba(255, 255, 255, 0.4)',
                              opacity: 1
                            }
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          type="text"
                          value={lastName || ''}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange(e, setLastName)}
                          placeholder="Last Name"
                          variant="outlined"
                          size="small"
                          fullWidth
                          sx={{
                            mt: 1,
                            '& .MuiOutlinedInput-root': {
                              color: 'rgba(255, 255, 255, 0.9)',
                              backgroundColor: 'rgba(255, 255, 255, 0.05)',
                              '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                              '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                              '&.Mui-focused fieldset': { borderColor: '#667eea' }
                            },
                            '& .MuiOutlinedInput-input::placeholder': {
                              color: 'rgba(255, 255, 255, 0.4)',
                              opacity: 1
                            }
                          }}
                        />
                      </Grid>
                    </Grid>
                    
                    {/* Legal Name (for DBS Update Service) - Only show if using DBS Update Service */}
                    {counsellor?.application?.legal?.dbsType === 'update_service' && (
                      <>
                        <Typography variant="caption" sx={{ display: 'block', mt: 2, mb: 1, color: 'rgba(255, 255, 255, 0.6)' }}>
                          Legal Name on DBS Certificate (if different from preferred name)
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              type="text"
                              value={legalFirstName || ''}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLegalFirstName && handleInputChange(e, setLegalFirstName)}
                              placeholder="Legal First Name (Optional)"
                              variant="outlined"
                              size="small"
                              fullWidth
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  color: 'rgba(255, 255, 255, 0.9)',
                                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                  '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                                  '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                                  '&.Mui-focused fieldset': { borderColor: '#667eea' }
                                },
                                '& .MuiOutlinedInput-input::placeholder': {
                                  color: 'rgba(255, 255, 255, 0.4)',
                                  opacity: 1
                                }
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              type="text"
                              value={legalLastName || ''}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLegalLastName && handleInputChange(e, setLegalLastName)}
                              placeholder="Legal Last Name (Optional)"
                              variant="outlined"
                              size="small"
                              fullWidth
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  color: 'rgba(255, 255, 255, 0.9)',
                                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                  '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                                  '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                                  '&.Mui-focused fieldset': { borderColor: '#667eea' }
                                },
                                '& .MuiOutlinedInput-input::placeholder': {
                                  color: 'rgba(255, 255, 255, 0.4)',
                                  opacity: 1
                                }
                              }}
                            />
                          </Grid>
                        </Grid>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle1" fontWeight="bold" className="theme-text-primary" sx={{ mr: 1, minWidth: '150px' }}>Preferred Name:</Typography>
                      <Typography variant="body1" className={firstName || lastName ? 'theme-text-primary' : 'theme-text-secondary'}>
                        {firstName && lastName ? `${firstName} ${lastName}` : (firstName || lastName || 'Not provided')}
                      </Typography>
                    </Typography>
                    {counsellor?.application?.legal?.dbsType === 'update_service' && (legalFirstName || legalLastName) && (
                      <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="subtitle1" fontWeight="bold" className="theme-text-primary" sx={{ mr: 1, minWidth: '150px' }}>Legal Name:</Typography>
                        <Typography variant="body1" className="theme-text-primary">
                          {legalFirstName || ''} {legalLastName || ''}
                        </Typography>
                      </Typography>
                    )}
                  </>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                {isEditingPersonal ? (
                  <>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom className="theme-text-primary">Email</Typography>
                    {renderInput(email, setEmail, 'email', '', true)}
                    <Typography variant="caption" className="theme-text-secondary" sx={{ mt: 0.5, display: 'block' }}>
                      Email cannot be changed
                    </Typography>
                  </>
                ) : (
                  <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="subtitle1" fontWeight="bold" className="theme-text-primary" sx={{ mr: 1, minWidth: '80px' }}>Email:</Typography>
                    <Typography variant="body1" className={email ? 'theme-text-primary' : 'theme-text-secondary'}>
                      {email || 'Not provided'}
                    </Typography>
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                {isEditingPersonal ? (
                  <>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom className="theme-text-primary">Phone</Typography>
                    {renderInput(phone, setPhone, 'tel')}
                  </>
                ) : (
                  <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="subtitle1" fontWeight="bold" className="theme-text-primary" sx={{ mr: 1, minWidth: '80px' }}>Phone:</Typography>
                    <Typography variant="body1" className={phone ? 'theme-text-primary' : 'theme-text-secondary'}>
                      {phone || 'Not provided'}
                    </Typography>
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                {isEditingPersonal ? (
                  <>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom className="theme-text-primary">Date of Birth</Typography>
                    <Box sx={{ mt: 1 }}>
                      <DatePicker
                        selected={dob ? new Date(dob) : null}
                        onChange={handleDateChange}
                        dateFormat="dd/MM/yyyy"
                        placeholderText="Select date"
                        className="mui-datepicker"
                      />
                    </Box>
                  </>
                ) : (
                  <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="subtitle1" fontWeight="bold" className="theme-text-primary" sx={{ mr: 1, minWidth: '80px' }}>DOB:</Typography>
                    <Typography variant="body1" className={dob ? 'theme-text-primary' : 'theme-text-secondary'}>
                      {dob ? new Date(dob).toLocaleDateString('en-GB') : 'Not provided'}
                    </Typography>
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                {isEditingPersonal ? (
                  <>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom className="theme-text-primary">Gender</Typography>
                    <FormControl fullWidth sx={{ mt: 1 }}>
                      <Select
                        value={gender || ''}
                        onChange={(e) => setGender && setGender(e.target.value)}
                        displayEmpty
                        size="small"
                        sx={{
                          backgroundColor: 'rgba(0, 0, 0, 0.2)',
                          '& .MuiSelect-select': { color: 'white', padding: '8px 14px' },
                          '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6366f1' },
                          '& .MuiSvgIcon-root': { color: 'rgba(255, 255, 255, 0.7)' }
                        }}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              bgcolor: '#1e293b',
                              '& .MuiMenuItem-root': {
                                color: 'white',
                                '&:hover': { bgcolor: 'rgba(99, 102, 241, 0.1)' },
                                '&.Mui-selected': { 
                                  bgcolor: 'rgba(99, 102, 241, 0.2)',
                                  '&:hover': { bgcolor: 'rgba(99, 102, 241, 0.3)' }
                                }
                              }
                            }
                          }
                        }}
                      >
                        <MenuItem value=""><em>Select Gender</em></MenuItem>
                        {lookups.gender.map((lookup) => (
                          <MenuItem key={lookup._id} value={lookup.value}>
                            {lookup.displayName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </>
                ) : (
                  <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="subtitle1" fontWeight="bold" className="theme-text-primary" sx={{ mr: 1, minWidth: '80px' }}>Gender:</Typography>
                    <Typography variant="body1" className={gender ? 'theme-text-primary' : 'theme-text-secondary'}>
                      {gender ? lookups.gender.find(g => g.value === gender)?.displayName || gender.charAt(0).toUpperCase() + gender.slice(1) : 'Not provided'}
                    </Typography>
                  </Typography>
                )}
              </Grid>
            </Grid>
            
            {/* Divider between personal info and address */}
            <Box sx={{ my: 4, borderTop: '1px solid rgba(255, 255, 255, 0.2)' }} />
            
            {/* Address Information Section */}
            <Grid container spacing={2}>
              {isEditingPersonal ? (
                <Grid item xs={12}>
                  <AddressAutocomplete
                    value={{
                      addressLine1: addressLine1 || '',
                      addressLine2: addressLine2 || '',
                      addressLine3: addressLine3 || '',
                      city: city || '',
                      county: county || '',
                      postcode: postcode || '',
                      country: country || ''
                    }}
                    onChange={(address) => {
                      setAddressLine1(address.addressLine1 || '');
                      setAddressLine2(address.addressLine2 || '');
                      setAddressLine3?.(address.addressLine3 || '');
                      setCity(address.city || '');
                      setCounty(address.county || '');
                      setPostcode(address.postcode || '');
                      if (address.country) setCountry(address.country);
                    }}
                    fieldMapping={{
                      line1: 'addressLine1',
                      line2: 'addressLine2',
                      line3: 'addressLine3',
                      city: 'city',
                      postcode: 'postcode',
                      county: 'county',
                      country: 'country'
                    }}
                    required={false}
                    label="Counsellor Address"
                  />
                </Grid>
              ) : (
                <>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="subtitle1" fontWeight="bold" className="theme-text-primary" sx={{ mr: 1, minWidth: '120px' }}>Address 1:</Typography>
                      <Typography variant="body1" className={addressLine1 ? 'theme-text-primary' : 'theme-text-secondary'}>
                        {addressLine1 || 'Not provided'}
                      </Typography>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="subtitle1" fontWeight="bold" className="theme-text-primary" sx={{ mr: 1, minWidth: '120px' }}>Address 2:</Typography>
                      <Typography variant="body1" className={addressLine2 ? 'theme-text-primary' : 'theme-text-secondary'}>
                        {addressLine2 || 'Not provided'}
                      </Typography>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="subtitle1" fontWeight="bold" className="theme-text-primary" sx={{ mr: 1, minWidth: '120px' }}>Address 3:</Typography>
                      <Typography variant="body1" className={addressLine3 ? 'theme-text-primary' : 'theme-text-secondary'}>
                        {addressLine3 || 'Not provided'}
                      </Typography>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="subtitle1" fontWeight="bold" className="theme-text-primary" sx={{ mr: 1, minWidth: '120px' }}>City:</Typography>
                      <Typography variant="body1" className={city ? 'theme-text-primary' : 'theme-text-secondary'}>
                        {city || 'Not provided'}
                      </Typography>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="subtitle1" fontWeight="bold" className="theme-text-primary" sx={{ mr: 1, minWidth: '120px' }}>County:</Typography>
                      <Typography variant="body1" className={county ? 'theme-text-primary' : 'theme-text-secondary'}>
                        {county || 'Not provided'}
                      </Typography>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="subtitle1" fontWeight="bold" className="theme-text-primary" sx={{ mr: 1, minWidth: '120px' }}>Postcode:</Typography>
                      <Typography variant="body1" className={postcode ? 'theme-text-primary' : 'theme-text-secondary'}>
                        {postcode || 'Not provided'}
                      </Typography>
                    </Typography>
                  </Grid>
                </>
              )}
              <Grid item xs={12} sm={6}>
                {isEditingPersonal ? (
                  <>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom className="theme-text-primary">Country</Typography>
                    {renderInput(country, setCountry)}
                  </>
                ) : (
                  <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="subtitle1" fontWeight="bold" className="theme-text-primary" sx={{ mr: 1, minWidth: '120px' }}>Country:</Typography>
                    <Typography variant="body1" className={country ? 'theme-text-primary' : 'theme-text-secondary'}>
                      {country || 'Not provided'}
                    </Typography>
                  </Typography>
                )}
              </Grid>
            </Grid>
            
            {/* Divider between address and desirables */}
            <Box sx={{ my: 4, borderTop: '1px solid rgba(255, 255, 255, 0.2)' }} />
            
            {/* Underrepresented Categories (Desirables) Section */}
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle1" className="theme-text-primary" fontWeight="bold" gutterBottom>
                  Desirables (Underrepresented Categories)
                </Typography>
                {counsellor.underrepresentedCategories && counsellor.underrepresentedCategories.length > 0 ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                    {counsellor.underrepresentedCategories.map((category, index) => (
                      <Chip
                        key={index}
                        label={category}
                        sx={{ 
                          backgroundColor: '#e3f2fd',
                          color: '#1976d2',
                          fontWeight: 'medium'
                        }}
                      />
                    ))}
                  </Box>
                ) : (
                  <Typography className="theme-text-secondary" sx={{ mt: 1 }}>None specified</Typography>
                )}
              </Grid>
            </Grid>
          </div>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PersonalInformation;
